import { useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import { ChevronDown, ChevronUp, Inbox, UserRound, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Profile } from "@/reducers/profilesSlice"
import {
  firstPickupStop,
  lastDeliveryStop,
  PlannedOrder,
} from "@/reducers/plannedOrdersSlice"
import { plannedOrderFlags } from "./plannerFlags"
import { matchesCompanyAndDispatcherFilter } from "./plannerFilters"
import { orderReferenceNumber as referenceNumber } from "./plannerLabels"

export const matchesOrderQuery = (
  order: PlannedOrder,
  query: string,
): boolean => {
  const normalizedQuery = query.trim().toLowerCase()
  if (normalizedQuery === "") return true

  const haystacks = [
    referenceNumber(order),
    order.metadata?.customer_name,
    firstPickupStop(order)?.location.formattedAddress,
    firstPickupStop(order)?.location.locality,
    lastDeliveryStop(order)?.location.formattedAddress,
    lastDeliveryStop(order)?.location.locality,
  ]
  return haystacks.some((value) =>
    (value ?? "").toLowerCase().includes(normalizedQuery),
  )
}

type PlannedOrdersPanelProps = {
  unassignedOrders: PlannedOrder[]
  myOrders: PlannedOrder[]
  dispatcherAssignedOrders: PlannedOrder[]
  dispatchers: Profile[]
  assignedCountByDispatcher: Record<string, number>
  currentUserJid?: string
  isPlannerRoleEnabled: boolean
  // Lifted to the parent so the same selection also filters the vehicle
  // rows on the left of the timeline.
  companyFilter: string[]
  onCompanyFilterChange: (companies: string[]) => void
  dispatcherFilter: string[]
  onDispatcherFilterChange: (dispatchers: string[]) => void
  // One search box for the whole planner, as in the mockup: the same text
  // filters these lists and the vehicle rows on the left. Owned by the parent,
  // which renders the input in the filter bar above the grid.
  query: string
  onQueryChange: (query: string) => void
  // Row click opens the inline detail panel (PlannedOrderDetailPanel),
  // which carries both the dispatcher-assign and vehicle-assign actions.
  onSelectOrder: (order: PlannedOrder) => void
}

const stopSummary = (order: PlannedOrder) => {
  const pickup = firstPickupStop(order)
  const delivery = lastDeliveryStop(order)
  const label = (stop: typeof pickup) =>
    stop?.location.locality || stop?.location.formattedAddress || "—"
  const time = (stop: typeof pickup) => {
    if (!stop?.scheduled_arrival_time) return ""
    const parsed = parseISO(stop.scheduled_arrival_time)
    if (Number.isNaN(parsed.getTime())) return ""
    return format(parsed, "dd.MM HH:mm")
  }
  return {
    pickupLabel: label(pickup),
    pickupTime: time(pickup),
    deliveryLabel: label(delivery),
    deliveryTime: time(delivery),
  }
}

const OrderRow = ({
  order,
  assignedDispatcherName,
  onSelectOrder,
}: {
  order: PlannedOrder
  assignedDispatcherName?: string
  onSelectOrder: (order: PlannedOrder) => void
}) => {
  const { t } = useTranslation()
  const summary = stopSummary(order)
  const flags = plannedOrderFlags(order)
  return (
    <button
      type="button"
      onClick={() => onSelectOrder(order)}
      className="grid w-full grid-cols-[20px_1fr_20px] items-start gap-2 border-b px-3 py-2.5 text-left text-sm hover:bg-muted/60"
    >
      <span aria-hidden className="text-base leading-none">
        {flags.from}
      </span>
      <span className="min-w-0">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate font-semibold">
            {referenceNumber(order) || t("planner.common.order")}
          </span>
          {order.metadata?.customer_name && (
            <span className="shrink-0 truncate text-xs text-muted-foreground">
              {order.metadata.customer_name}
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {summary.pickupLabel}
          {summary.pickupTime && ` • ${summary.pickupTime}`}
          {" → "}
          {summary.deliveryLabel}
          {summary.deliveryTime && ` • ${summary.deliveryTime}`}
        </span>
        {assignedDispatcherName && (
          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
            <UserRound className="mr-1 inline h-3 w-3" />
            {assignedDispatcherName}
          </span>
        )}
      </span>
      <span aria-hidden className="text-right text-base leading-none">
        {flags.to}
      </span>
    </button>
  )
}

const PlannedOrdersPanel = ({
  unassignedOrders,
  myOrders,
  dispatcherAssignedOrders,
  dispatchers,
  assignedCountByDispatcher,
  currentUserJid,
  isPlannerRoleEnabled,
  companyFilter,
  onCompanyFilterChange,
  dispatcherFilter,
  onDispatcherFilterChange,
  query,
  onQueryChange,
  onSelectOrder,
}: PlannedOrdersPanelProps) => {
  const { t } = useTranslation()
  // Two independent accordions: collapsing one gives the other the freed
  // vertical space (flex-1 vs shrink-0), so a dispatcher who only cares
  // about one list can hide the other instead of always splitting 50/50.
  const [isUnassignedExpanded, setIsUnassignedExpanded] = useState(true)
  const [isDispatcherAssignedExpanded, setIsDispatcherAssignedExpanded] =
    useState(true)

  const filteredUnassignedOrders = useMemo(
    () =>
      unassignedOrders.filter(
        (order) =>
          matchesOrderQuery(order, query) &&
          matchesCompanyAndDispatcherFilter(order, companyFilter, []),
      ),
    [unassignedOrders, query, companyFilter],
  )
  // "Moje do przypisania" only renders for a regular dispatcher (see below),
  // who never sees the company/dispatcher filter controls — so unlike the
  // other two lists, this one only ever needs the search query.
  const filteredMyOrders = useMemo(
    () => myOrders.filter((order) => matchesOrderQuery(order, query)),
    [myOrders, query],
  )
  const filteredDispatcherAssignedOrders = useMemo(
    () =>
      dispatcherAssignedOrders.filter(
        (order) =>
          matchesOrderQuery(order, query) &&
          matchesCompanyAndDispatcherFilter(
            order,
            companyFilter,
            dispatcherFilter,
          ),
      ),
    [dispatcherAssignedOrders, query, companyFilter, dispatcherFilter],
  )
  const hasActiveFilters =
    query !== "" || companyFilter.length > 0 || dispatcherFilter.length > 0
  const clearFilters = () => {
    onQueryChange("")
    onCompanyFilterChange([])
    onDispatcherFilterChange([])
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--panel-color)]">
      {/* The company/dispatcher selects and the search box moved up into the
          planner's single filter bar (mockup) — only the reset shortcut stays
          here, next to the lists it clears. */}
      {isPlannerRoleEnabled && hasActiveFilters && (
        <div className="flex items-center justify-end border-b px-3 py-1.5">
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-3.5 w-3.5" />
            {t("planner.ordersPanel.clearFilters")}
          </Button>
        </div>
      )}

      {isPlannerRoleEnabled ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
          {/* Nieprzypisane zlecenia — top accordion, expands to flex-1 or
              collapses to just its header (shrink-0), mirroring the
              andrzej/planing-tool mockup's two independent accordions. */}
          <Card
            className={cn(
              "flex min-h-0 flex-col overflow-y-auto",
              isUnassignedExpanded ? "flex-1" : "shrink-0",
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-[var(--panel-color)] px-3 py-2">
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <Inbox className="h-4 w-4" />
                {t("planner.ordersPanel.unassignedOrders")}
                <Badge variant="secondary">
                  {filteredUnassignedOrders.length}
                </Badge>
              </p>
              <button
                type="button"
                aria-label={
                  isUnassignedExpanded
                    ? t("planner.ordersPanel.collapse")
                    : t("planner.ordersPanel.expand")
                }
                className="text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => setIsUnassignedExpanded((value) => !value)}
              >
                {isUnassignedExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>
            {isUnassignedExpanded &&
              (filteredUnassignedOrders.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                  {unassignedOrders.length === 0
                    ? t("planner.ordersPanel.noOrdersInPool")
                    : t("planner.ordersPanel.noMatchingOrders")}
                </p>
              ) : (
                filteredUnassignedOrders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onSelectOrder={onSelectOrder}
                  />
                ))
              ))}
          </Card>

          {/* Przypisane do dyspozytora — bottom accordion. Already the full
              cross-dispatcher set (Task 3), so it includes the planner's own
              parked orders too — no separate "Moje do przypisania" needed
              here. */}
          <Card
            className={cn(
              "flex min-h-0 flex-col overflow-y-auto",
              isDispatcherAssignedExpanded ? "flex-1" : "shrink-0",
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-[var(--panel-color)] px-3 py-2">
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <UserRound className="h-4 w-4" />
                {t("planner.ordersPanel.assignedToDispatcher")}
                <Badge variant="secondary">
                  {filteredDispatcherAssignedOrders.length}
                </Badge>
              </p>
              <button
                type="button"
                aria-label={
                  isDispatcherAssignedExpanded
                    ? t("planner.ordersPanel.collapse")
                    : t("planner.ordersPanel.expand")
                }
                className="text-muted-foreground transition-colors hover:text-foreground"
                onClick={() =>
                  setIsDispatcherAssignedExpanded((value) => !value)
                }
              >
                {isDispatcherAssignedExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>
            {/* Cross-dispatcher workload, next to the list it describes rather
                than as a wall of names closing the whole panel. Planner-only: a
                regular dispatcher gets their own count from "Moje do
                przypisania", so a lone self-badge here would just be a second,
                differently-scoped number for the same person. */}
            {isDispatcherAssignedExpanded && dispatchers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b px-3 py-2">
                {dispatchers.map((dispatcher) => (
                  <Badge
                    key={dispatcher.jid}
                    variant={
                      dispatcher.jid === currentUserJid ? "default" : "outline"
                    }
                  >
                    {dispatcher.name || dispatcher.jid}:{" "}
                    {assignedCountByDispatcher[dispatcher.jid] ?? 0}
                  </Badge>
                ))}
              </div>
            )}
            {isDispatcherAssignedExpanded &&
              (filteredDispatcherAssignedOrders.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                  {t("planner.ordersPanel.noDispatcherOrders")}
                </p>
              ) : (
                filteredDispatcherAssignedOrders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    assignedDispatcherName={
                      dispatchers.find(
                        (dispatcher) =>
                          dispatcher.jid === order.assignedDispatcherJid,
                      )?.name
                    }
                    onSelectOrder={onSelectOrder}
                  />
                ))
              ))}
          </Card>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b bg-[var(--panel-color)] px-3 py-2 text-sm font-semibold">
            <UserRound className="h-4 w-4" />
            {t("planner.ordersPanel.mineToAssign")}
            <Badge variant="secondary">{filteredMyOrders.length}</Badge>
          </div>
          {filteredMyOrders.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {t("planner.ordersPanel.noOrdersToAssign")}
            </p>
          ) : (
            filteredMyOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                onSelectOrder={onSelectOrder}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default PlannedOrdersPanel
